"use client";

import React from "react";
import { ArrowRight, Atom, Waves, GitMerge, Orbit } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Reveal } from "@/components/cosmic/Reveal";
interface QuantumPillar {
  title: string;
  desc: string;
  icon: typeof Atom;
  symbol: string;
  renderVisual: () => React.ReactNode;
}
const PILLARS: QuantumPillar[] = [{
  title: "Superposition",
  desc: "Particles navigate multiple simultaneous probability states until physical interaction forces a definite outcome.",
  icon: Atom,
  symbol: "|Ψ⟩ = α|0⟩ + β|1⟩",
  renderVisual: () => <div className="relative flex size-28 items-center justify-center">
        <div className="absolute inset-0 rounded-full border border-primary/20 animate-spin [animation-duration:14s]" />
        <div className="absolute inset-2 rounded-full border border-sky-400/30 animate-spin [animation-direction:reverse] [animation-duration:10s]" />
        <div className="absolute size-3 rounded-full bg-primary animate-pulse" />
        <div className="absolute size-2 rounded-full bg-sky-400 -top-1 left-8 opacity-75" />
        <div className="absolute size-2 rounded-full bg-primary/70 -bottom-1 right-8 opacity-75" />
      </div>
}, {
  title: "Wave Interference",
  desc: "Probability amplitudes interfere constructively and destructively, proving light and matter display wave-particle duality.",
  icon: Waves,
  symbol: "I = |ψ₁ + ψ₂|²",
  renderVisual: () => <div className="relative flex size-28 items-center justify-center overflow-hidden">
        <div className="absolute inset-x-2 flex flex-col justify-between h-20 opacity-80">
          <div className="h-0.5 w-full bg-gradient-to-r from-transparent via-primary to-transparent" />
          <div className="h-0.5 w-full bg-gradient-to-r from-transparent via-sky-400 to-transparent" />
          <div className="h-0.5 w-full bg-gradient-to-r from-transparent via-primary/80 to-transparent" />
          <div className="h-0.5 w-full bg-gradient-to-r from-transparent via-sky-400/70 to-transparent" />
          <div className="h-0.5 w-full bg-gradient-to-r from-transparent via-primary/60 to-transparent" />
        </div>
        <div className="absolute size-14 rounded-full border border-primary/40 bg-primary/5" />
      </div>
}, {
  title: "Quantum Entanglement",
  desc: "Spatially separated particles remain correlated such that measuring one determines the state of its paired partner.",
  icon: GitMerge,
  symbol: "ρ_AB ≠ ∑ pᵢ ρ^A_i ⊗ ρ^B_i",
  renderVisual: () => <div className="relative flex size-28 items-center justify-center">
        <div className="absolute w-20 h-px bg-gradient-to-r from-primary via-white to-sky-400 opacity-70" />
        <div className="absolute left-2 size-4 rounded-full border border-primary bg-primary/20 flex items-center justify-center">
          <div className="size-1.5 rounded-full bg-primary" />
        </div>
        <div className="absolute right-2 size-4 rounded-full border border-sky-400 bg-sky-400/20 flex items-center justify-center">
          <div className="size-1.5 rounded-full bg-sky-300" />
        </div>
        <div className="absolute size-12 rounded-full border border-dashed border-primary/25 animate-spin [animation-duration:20s]" />
      </div>
}, {
  title: "Probability Densities",
  desc: "Definite trajectories yield to statistical wave functions describing where a subatomic entity is most likely to materialize.",
  icon: Orbit,
  symbol: "P(x) = |ψ(x)|² dx",
  renderVisual: () => <div className="relative flex size-28 items-center justify-center">
        <div className="absolute size-24 rounded-full bg-radial from-primary/25 via-sky-500/10 to-transparent blur-xs" />
        <div className="absolute size-16 rounded-full border border-primary/30 border-dashed" />
        <div className="absolute size-8 rounded-full border border-primary/60" />
        <div className="absolute size-2 rounded-full bg-primary" />
      </div>
}];
export default function QuantumLab() {
  return <section id="quantum-lab" data-nav="dark" className="relative py-16 sm:py-20 lg:py-28 w-full max-w-full overflow-x-clip text-foreground">
      <div className="relative z-10 mx-auto max-w-[76rem] w-full px-4 sm:px-6">
        {/* Subtle Section divider */}
        <div className="ql-divider mb-16" aria-hidden="true" />

        {/* Split-Stage Composition: Left Column (1-4) sticky header & analytical prose, Right Column (5-12) 4-matrix cards */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-start">
          {/* Columns 1-4: Sticky Header & Narrative */}
          <div className="lg:col-span-4 lg:sticky lg:top-24 flex flex-col items-start w-full min-w-0">
            <Reveal>
              <div className="inline-flex items-center gap-2 mb-4 font-mono text-xs font-semibold tracking-widest text-primary uppercase">
                <Atom className="size-3.5" />
                <span>INTERACTIVE MECHANICS</span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight font-serif text-foreground leading-[1.12] break-words">
                Quantum Foundations Under the Microscope
              </h2>

              <p className="mt-5 text-base text-muted-foreground leading-relaxed font-sans">
                Step beyond macro-scale intuitions. Explore four core pillars of quantum mechanics through simplified conceptual models and calibrated simulations.
              </p>

              <div className="mt-8 w-full sm:w-auto">
                <Link to="/QuantumLabPage" className="ql-btn w-full sm:w-auto min-h-[44px] justify-center">
                  <span>Open Quantum Lab</span>
                  <ArrowRight className="size-4 shrink-0" />
                </Link>
              </div>
            </Reveal>
          </div>

          {/* Columns 5-12: 2x2 Matrix Cards */}
          <div className="lg:col-span-8 w-full min-w-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {PILLARS.map((pillar, i) => {
              const Icon = pillar.icon;
              return <Reveal key={pillar.title} delay={i * 90} className="h-full">
                    <div className="ql-card p-6 h-full flex flex-col justify-between items-center text-center group">
                      {/* Top indicator hairline on hover */}
                      <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-primary/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                      <div className="flex flex-col items-center">
                        {/* Glowing particle illustration */}
                        <div className="mb-6 mt-2 flex items-center justify-center">
                          {pillar.renderVisual()}
                        </div>

                        {/* Mathematical node formula label */}
                        <div className="mb-3 max-w-full overflow-hidden text-ellipsis inline-block rounded-md border border-border/60 bg-muted/70 px-2.5 py-1 font-mono text-[11px] text-muted-foreground tracking-wider break-all">
                          {pillar.symbol}
                        </div>

                        <div className="flex items-center justify-center gap-2 mb-2">
                          <Icon className="size-4 text-primary" strokeWidth={1.75} />
                          <h3 className="font-serif text-lg font-bold text-card-foreground">
                            {pillar.title}
                          </h3>
                        </div>

                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {pillar.desc}
                        </p>
                      </div>
                    </div>
                  </Reveal>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}